import { graphql, withApollo } from 'react-apollo'

import debounceHandler from '@hocs/debounce-handler'
import { withFormik } from 'formik'
import { compose, lifecycle, withHandlers, withProps, withState } from 'recompose'

import withPartnerProps from 'components/HOCs/WithPartnerProps'
import withRemoteStrings from 'components/HOCs/withRemoteStrings'
import defaults from 'components/Trackers/defaults'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { getButtonTappedEvent } from 'utils/analytics'
import trackEvent from 'utils/analytics/trackEvent'
import { withApolloErrorModal } from 'utils/Apollo'
import { withFlowRouter } from 'utils/FlowProvider'

import { handleFormSubmit, mapPropsToValues, validate } from './form'
import setPortfolio from './graphql/setPortfolio.mutation.gql'
import updateProfile from './graphql/updateProfile.mutation.gql'
import updateSuitability from './graphql/updateSuitability.mutation.gql'
import { InnerProps } from './types'
import { getRows, initializeSuitabilityState, setNextPage } from './utils'
import { getLocalState, updateAnswer } from './utils'

export const enhance = compose<InnerProps, {}>(
  withApollo,
  withApolloErrorModal,
  withFlowRouter,
  withPartnerProps,
  withRemoteStrings,
  graphql(updateProfile, { name: 'updateProfile' }),
  graphql(updateSuitability, { name: 'updateSuitability' }),
  graphql(setPortfolio, { name: 'setPortfolio' }),
  withState('rows', 'setRows', [[]]),
  withProps(({ client, getRemoteString }) => {
    const { page, showNextButton, existingCoreCustomer } = getLocalState(client)

    const headerText = existingCoreCustomer
      ? getRemoteString('later_ira_questions_title', 'Which IRA type is right for you?')
      : page === 1
      ? getRemoteString('suitability_questions_1_header', 'Your finances')
      : getRemoteString('suitability_questions_2_header', 'Your goals')

    const subheaderText = existingCoreCustomer
      ? getRemoteString(
          'later_ira_questions_header',
          'One IRA type does not fit all, so we consider your age and goals, and ask the right questions to find the right fit for you!',
        )
      : page === 1
      ? getRemoteString(
          'suitability_questions_1_text',
          'Tell us a little about your current financial situation.',
        )
      : getRemoteString(
          'suitability_questions_2_text',
          "Now tell us what you'd like to accomplish with your investments.",
        )

    return {
      showNextButton,
      headerText,
      subheaderText,
    }
  }),
  withFormik({
    enableReinitialize: true,
    handleSubmit: handleFormSubmit,
    mapPropsToValues,
    validate,
  }),
  // Adding processRows as a separate handler definition from the other handlers
  // because debounceHandler doesn't seem to work when it is defined in the same
  // withHandlers block
  withHandlers({
    processRows: ({ client, setRows, validateForm }) => async e => {
      updateAnswer(client, e.target.name, e.target.value)
      const newRows = getRows(client)

      validateForm().then(errors => {
        // If no validation errors exist, then fetch the next row and render
        if (Object.keys(errors).length === 0) {
          setRows(newRows)
        } else {
          // Don't show any rows after the error (truncate array)
          const i = newRows[0].findIndex(row => row.name === Object.keys(errors)[0])
          if (i !== -1) {
            newRows[0].length = i + 1
          }

          setRows(newRows)
          validateForm()
        }
      })
    },
  }),
  debounceHandler('processRows', 650),
  withHandlers({
    interceptHandleChange: ({ handleChange, processRows }) => async e => {
      const value = e.target.value.replace(/\s/g, '').trim()
      e.target.value = value

      await handleChange(e)
      processRows(e)
    },
    interceptSelectHandleChange: ({ client, setFieldValue, setRows }) => async (field, value) => {
      await setFieldValue(field, value)
      updateAnswer(client, field, value)
      setRows(getRows(client))
    },
    handleNextPressed: ({ client, handleSubmit, partner, setRows, flow }) => async () => {
      let eventData
      const { page, existingCoreCustomer } = getLocalState(client)
      if (existingCoreCustomer && flow === 'laterOnboarding') {
        eventData = getButtonTappedEvent(
          TRACKER_IDS.LATER_SUITABILITY_QUESTIONS.LATER_ONBOARDING_NEXT_BUTTON,
        )

        trackEvent('Button Tapped', {
          eventData: {
            ...eventData,
            ...defaults('Button Tapped'),
          },
          partner,
          flow,
        })
        return handleSubmit(client)
      }
      // Construct page-specific button tapped event
      eventData =
        page === 1
          ? getButtonTappedEvent(TRACKER_IDS.LATER_SUITABILITY_QUESTIONS.NEXT_BUTTON_1)
          : getButtonTappedEvent(TRACKER_IDS.LATER_SUITABILITY_QUESTIONS.NEXT_BUTTON_2)

      // Fire button tapped event
      trackEvent('Button Tapped', {
        eventData: {
          ...eventData,
          ...defaults('Button Tapped'),
        },
        partner,
        flow,
      })

      if (page === 1) {
        setNextPage(client)
        setRows(getRows(client))

        trackEvent('Screen Viewed', {
          eventData: {
            ...defaults('Screen Viewed'),
            object_name: 'registrationPortfolioQuestionsPartTwo',
            screen: 'registrationPortfolioQuestionsPartTwo',
            screen_name: 'registrationPortfolioQuestionsPartTwo',
          },
          partner,
          flow,
        })
      } else {
        handleSubmit(client)
      }
    },
  }),
  lifecycle<InnerProps, {}, {}>({
    componentDidMount() {
      const client = this.props.client
      // Asynchronously fetch Core Suitability questions
      initializeSuitabilityState(client).then(() => this.props.setRows(getRows(client)))
    },
  }),
)

export default enhance
